import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Spinner,
  Button,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaUser, FaStore, FaCopy, FaUserFriends } from "react-icons/fa";

const ReferFriends = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      // Get admin token for authentication
      const adminToken = sessionStorage.getItem("adminToken");
      console.log("[ReferFriends] Admin token:", adminToken);

      if (!adminToken) {
        throw new Error("No admin token found. Please log in again.");
      }

      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      console.log("[ReferFriends] Base URLs:", bases);

      let data = null;
      let lastErr = null;

      // Try to fetch referrals from each base URL until one works
      for (const base of bases) {
        try {
          // The admin routes are mounted at /api/admins, so the referrals endpoint is /api/admins/referrals
          const url = `${base}/api/admins/referrals`;
          console.log("[ReferFriends] Fetching from URL:", url);

          const res = await fetch(url, {
            headers: {
              Authorization: `Bearer ${adminToken}`,
              "Content-Type": "application/json",
            },
          });

          console.log("[ReferFriends] Response status:", res.status);

          if (!res.ok) {
            const errorText = await res.text();
            console.error("[ReferFriends] Error response:", errorText);
            lastErr = new Error(
              `Failed to load referrals from ${base}: ${res.status} - ${errorText}`
            );
            continue;
          }

          data = await res.json();
          console.log("[ReferFriends] Loaded data from", base, ":", data);
          break;
        } catch (e) {
          console.error("[ReferFriends] Error fetching from", base, ":", e);
          lastErr = e;
          continue;
        }
      }

      if (!data)
        throw (
          lastErr || new Error("Failed to load referrals from any endpoint")
        );

      // Add debugging to see what data is being received
      if (data.referrals) {
        console.log(
          "[ReferFriends] Number of referrals:",
          data.referrals.length
        );
        if (data.referrals.length > 0) {
          console.log(
            "[ReferFriends] First referral sample:",
            data.referrals[0]
          );
        }
      }

      setReferrals(data.referrals || []);
      setLoading(false);
    } catch (e) {
      console.error("[ReferFriends] Failed", e);
      setError("Failed to load referrals: " + (e.message || "Unknown error"));
      setLoading(false);
    }
  };

  // Use the same icon for both referrer and referee
  const referralIcon = () => {
    return <FaUserFriends className="text-white" />;
  };

  const statusBadge = (status) => (
    <Badge
      bg={status === "successful" ? "success" : "secondary"}
      className="p-2"
    >
      {status.toUpperCase()}
    </Badge>
  );

  const copyReferralLink = () => {
    navigator.clipboard.writeText("https://app.example.com/referral/your-code");
    alert("Referral link copied to clipboard!");
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mx-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchReferrals}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div
      className="p-3"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-0 text-primary">Refer Friends</h1>
          <p className="text-muted">
            Earn rewards by referring friends and vendors to our platform
          </p>
        </div>
        <div>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Your Referral Program</h5>
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={8}>
                  <h6>Referral Tracking</h6>
                  <p className="text-muted small">
                    View all referral relationships between users and vendors on
                    the platform.
                  </p>
                </Col>
                <Col md={4} className="text-center">
                  <div className="p-3 bg-light rounded">
                    <h4 className="text-success">₹10 Credit</h4>
                    <p className="mb-0">For each successful referral</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Referral History</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Referrer</th>
                      <th>Referrer Email</th>
                      <th>Referrer Pincode</th>
                      <th>Referral Date</th>
                      <th>Referee</th>
                      <th>Referee Email</th>
                      <th>Referee Pincode</th>
                      <th>Referee Joined</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((r) => (
                      <tr
                        key={r.id}
                        className={
                          r.status === "pending" ? "table-warning" : ""
                        }
                      >
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="icon-container bg-primary">
                              {referralIcon()}
                            </div>
                            <div className="ms-3">
                              <div className="fw-bold">{r.referrerName}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {r.referrerEmail || r.referrerEmail === ""
                            ? r.referrerEmail
                            : "—"}
                        </td>
                        <td>{r.referrerPincode || "—"}</td>
                        <td className="text-nowrap">
                          {r.referralDate
                            ? new Date(r.referralDate).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="icon-container bg-primary">
                              {referralIcon()}
                            </div>
                            <div className="ms-3">
                              <div className="fw-bold">{r.refereeName}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {r.refereeEmail || r.refereeEmail === ""
                            ? r.refereeEmail
                            : "—"}
                        </td>
                        <td>{r.refereePincode || "—"}</td>
                        <td className="text-nowrap">
                          {r.refereeJoinDate
                            ? new Date(r.refereeJoinDate).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>{statusBadge(r.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>{`
        .icon-container {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .table th {
          border-top: none;
          font-weight: 600;
          color: #6c757d;
          padding: 1rem 0.75rem;
        }
        
        .table td {
          padding: 1rem 0.75rem;
          vertical-align: middle;
        }
        
        .card {
          border-radius: 12px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ReferFriends;
